import React from "react";
import PropTypes from "prop-types";

import { getMediaUrl } from "./media";
import { trackEvent } from "./analytics";
import { useLandingVideoContext } from "./LandingVideoContext";
import { shouldOpenVideoModal } from "./videoEmbed";

export const DEFAULT_MENU_ITEMS = [
  { title: "The Product", url: "#product" },
  { title: "Who it's for", url: "#audience" },
  { title: "How it works", url: "#how-it-works" },
  { title: "Use cases", url: "#use-cases" },
  { title: "Tutorials", url: "#tutorials" },
  { title: "Contact", url: "#contact" },
];

export const DEFAULT_PRIMARY_BUTTON = {
  title: "Download Plugin",
  url: "#download",
  target: "_self",
};

export const DEFAULT_SECONDARY_BUTTON = {
  title: "Contact Us",
  url: "#contact",
  target: "_self",
};

// ACF can deliver menu rows either flat or wrapped in a `menuItem` group;
// normalize to a flat list and fall back to the defaults when empty.
export const normalizeMenuItems = (items) => {
  if (!Array.isArray(items)) return DEFAULT_MENU_ITEMS;

  const normalized = items
    .map((item) => item?.menuItem || item)
    .filter((item) => item?.url && item?.title);

  return normalized.length ? normalized : DEFAULT_MENU_ITEMS;
};

export const LandingLink = ({ item, className, onClick, style, track, children }) => {
  if (!item?.url || !item?.title) return null;

  const videoContext = useLandingVideoContext();
  const autoVideoUrl = videoContext
    ? shouldOpenVideoModal(item, videoContext.video?.videoUrl)
    : null;
  const target = item.target || "_self";

  const handleClick = (e) => {
    if (track?.action) trackEvent(track.action, track);
    if (onClick) onClick(e);

    if (autoVideoUrl && videoContext?.openVideoModal) {
      e.preventDefault();
      videoContext.openVideoModal(autoVideoUrl);
    }
  };

  return (
    <a
      className={className}
      href={item.url}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      onClick={onClick || autoVideoUrl || track?.action ? handleClick : undefined}
      style={style}
    >
      {children || item.title}
    </a>
  );
};

// `baseClass` is the layout block (e.g. "landing_layout_header") so the same
// logo markup yields the header/footer-specific class names the CSS expects.
export const LandingLogo = ({ logo, baseClass }) => {
  const logoUrl = getMediaUrl(logo);

  return (
    <a className={`${baseClass}__logo`} href="/" aria-label="AI Custom Blocks">
      {logoUrl ? (
        <img src={logoUrl} alt={logo?.altText || "AI Custom Blocks"} />
      ) : (
        <span className={`${baseClass}__logo_fallback`}>
          <span className={`${baseClass}__logo_mark`} aria-hidden="true" />
          <span>
            <strong>AI</strong> Custom Blocks
          </span>
        </span>
      )}
    </a>
  );
};

const linkShape = PropTypes.shape({
  target: PropTypes.string,
  title: PropTypes.string,
  url: PropTypes.string,
});

const logoShape = PropTypes.shape({
  altText: PropTypes.string,
  mediaItemUrl: PropTypes.string,
  sourceUrl: PropTypes.string,
  url: PropTypes.string,
});

LandingLink.propTypes = {
  item: linkShape,
  className: PropTypes.string,
  onClick: PropTypes.func,
  style: PropTypes.object,
  children: PropTypes.node,
  // GA4 event fired on click: { action, category, label }. Non-blocking.
  track: PropTypes.shape({
    action: PropTypes.string,
    category: PropTypes.string,
    label: PropTypes.string,
  }),
};

LandingLink.defaultProps = {
  item: null,
  className: undefined,
  onClick: undefined,
  style: undefined,
  children: undefined,
  track: null,
};

LandingLogo.propTypes = {
  logo: logoShape,
  baseClass: PropTypes.string.isRequired,
};

LandingLogo.defaultProps = {
  logo: null,
};

LandingLink.shape = linkShape;
LandingLogo.shape = logoShape;
